<script>
  import { axisLeft, axisBottom } from 'd3-axis';
  import { select } from 'd3-selection';
  import { apdate } from 'journalize';
  import { thresholdTexture } from '$lib/states/index.svelte';
  import { valueType, getThresholdValue } from '$lib/constants/crosswalk';

  // light gray fill for the below-threshold band — lighter than the #cccccc
  // used for grayed-out, sub-threshold lines
  const thresholdFill = '#f1f1f2';
  thresholdTexture.value = thresholdFill;

  let { w, h, margin, chartwidth, xScale, yScale, dateExtent } = $props();

  let yAxis = $state();
  let xAxis = $state();

  $effect(() => {
    if (w && xScale && yAxis && xAxis) {
      const yAxisFormatter = axisLeft(yScale)
        .ticks(4)
        .tickFormat((d, i) => {
          if (i === 0) {
            return valueType === 'percent' ? `${d}%` : `${d} seats`;
          } else {
            return d;
          }
        })
        .tickSize(-w + margin.left + margin.right || 0);

      select(yAxis).call(yAxisFormatter);
      // Move labels inside the plot, over their gridlines (default vertical
      // centering already sits text right on the line), instead of out in
      // the left margin — lets the chart start right at the left edge.
      select(yAxis)
        .selectAll('.tick text')
        .attr('text-anchor', 'start')
        .attr('x', 4);

      // Trim each gridline so it starts right after its label ends, instead
      // of running underneath the label text.
      select(yAxis)
        .selectAll('.tick')
        .each(function () {
          const tick = select(this);
          const textNode = tick.select('text').node();
          const labelWidth = textNode ? textNode.getBBox().width : 0;
          tick.select('line').attr('x1', 4 + labelWidth + 8);
        });

      const xAxisFormatter = axisBottom(xScale)
        .tickValues([dateExtent[0]])
        .tickFormat(apdate);

      select(xAxis).call(xAxisFormatter);
    }
  });
</script>

<svg width={w} height={h}>
  {#if chartwidth && margin.left}
    <g class="threshold" transform="translate({margin.left}, {margin.top})">
      <rect
        width={chartwidth}
        height={yScale(0) - yScale(getThresholdValue()) + 6}
        x="0"
        y={yScale(getThresholdValue())}
        fill={thresholdFill}
      ></rect>
      <text class="threshold-label" x="5" y={yScale(getThresholdValue()) + 5}
        >Seat threshold</text
      >
    </g>
    <g
      class="axis y-axis"
      bind:this={yAxis}
      transform="translate({margin.left}, {margin.top})"
    ></g>
    <g
      class="axis x-axis"
      bind:this={xAxis}
      transform="translate({margin.left}, {h - margin.bottom})"
    ></g>
  {/if}
</svg>

<style lang="scss">
  .threshold-label {
    font-size: 11px;
    fill: #5c5c5c;
    alignment-baseline: hanging;
  }
  :global {
    .axis {
      line {
        stroke: #ccc;
      }
      .domain {
        display: none;
      }
      text {
        font-size: 13px;
        fill: #666;
      }
    }
    .y-axis .tick text {
      // white halo so the label stays legible sitting on top of its gridline
      paint-order: stroke fill;
      stroke: #fff;
      stroke-width: 4px;
      stroke-linejoin: round;
    }
    .x-axis .tick {
      text {
        text-anchor: start;
        position: relative;
        &::after {
          position: absolute;
          left: 0;
          right: 0;
          bottom: -4px;
          display: block;
          height: 3px;
          background: #cccccc;
          width: 90%;
          margin: auto;
        }
      }
    }
  }
</style>
